import { EmployeeFormValues } from '@/components/employees_tsx/employee-schema';
import { RealtimePostgresUpdatePayload } from '@supabase/supabase-js';

import supabase from './supabase';
import supabaseAdmin from './supabaseAdmin';

// Types for API responses
export interface Employee extends EmployeeFormValues {
  id: number;
  empUserId: string;
  created_at: string;
  position: number;
}

// Query keys for Tanstack Query
export const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (filters: string) => [...employeeKeys.lists(), { filters }] as const,
  details: () => [...employeeKeys.all, 'detail'] as const,
  detail: (id: number) => [...employeeKeys.details(), id] as const,
};

// Function to get all employees from the database
export async function getEmployees(): Promise<Employee[]> {
  const { data, error } = await supabase
    .from('employees')
    .select('*')
    .order('position', { ascending: true });

  if (error) {
    console.error('Error loading employees:', error);
    throw new Error('Could not load employees');
  }

  return data;
}

// Function to get a single employee by ID
export async function getEmployeeById(id: number): Promise<Employee> {
  const { data, error } = await supabase.from('employees').select('*').eq('id', id).single();

  if (error) {
    console.error('Error loading employee:', error);
    throw new Error('Could not load employee');
  }

  return data;
}

// Function to create a new Supabase auth user
async function createAuthUser(email: string, password: string = 'EzSalon@2024') {
  try {
    // Create the user with automatic confirmation and disable email sending
    const { data: authUser, error: authError } = await supabaseAdmin.auth.admin.createUser({
      email,
      password,
      email_confirm: true, // Automatically confirm the email
      user_metadata: {
        is_employee: true,
      },
      // This will prevent sending confirmation emails
      ban_duration: '0', // Not banned but prevents email sending
    });

    if (authError) {
      console.error('Error creating auth user:', authError);
      throw new Error('Could not create user account');
    }

    // Double-check that the user is created and confirmed
    const {
      data: { users },
      error: getUserError,
    } = await supabaseAdmin.auth.admin.listUsers();
    const createdUser = users?.find(u => u.id === authUser.user.id);

    if (getUserError || !createdUser?.email_confirmed_at) {
      // If user isn't properly confirmed, clean up and throw error
      await supabaseAdmin.auth.admin.deleteUser(authUser.user.id);
      throw new Error('Could not confirm user account');
    }

    return authUser.user;
  } catch (error) {
    console.error('Error in createAuthUser:', error);
    throw error;
  }
}

// Function to create or edit an employee
export async function createEditEmployee(
  employee: Partial<EmployeeFormValues>,
  id?: number
): Promise<Employee> {
  const query = supabase.from('employees');

  try {
    if (id) {
      // Get the employee data first to get the empUserId
      const { data: existingEmployee } = await query.select('*').eq('id', id).single();
      
      if (!existingEmployee) {
        throw new Error('Employee not found');
      }

      // Update existing employee
      const { data, error } = await query.update(employee).eq('id', id).select().single();

      if (error) {
        console.error('Error updating employee:', error);
        throw new Error('Could not update employee');
      }

      // If the name was updated, update it in both taskManager and taskManager_history tables
      if (employee.empName && employee.empName !== existingEmployee.empName) {
        // Update taskManager table
        const { error: taskError } = await supabase
          .from('taskManager')
          .update({ name: employee.empName })
          .eq('empUserId', existingEmployee.empUserId);

        if (taskError) {
          console.error('Error updating taskManager:', taskError);
          // Don't throw error here as the employee update was successful
        }

        // Update taskManager_history table
        const { error: historyError } = await supabase
          .from('taskManager_history')
          .update({ name: employee.empName })
          .eq('empUserId', existingEmployee.empUserId);

        if (historyError) {
          console.error('Error updating taskManager_history:', historyError);
          // Don't throw error here as the main updates were successful
        }
      }

      return data;
    } else {
      // For new employee, first create auth user
      const authUser = await createAuthUser(employee.empEmail!);

      // Create new employee with auth user ID
      const employeeData = {
        ...employee,
        empUserId: authUser.id,
      };

      const { data, error } = await query.insert(employeeData).select().single();

      if (error) {
        // If employee creation fails, delete the auth user
        await supabaseAdmin.auth.admin.deleteUser(authUser.id);
        console.error('Error creating employee:', error);
        throw new Error('Could not create employee');
      }

      return data;
    }
  } catch (error) {
    console.error('Operation failed:', error);
    throw error;
  }
}

// Function to delete an employee by empUserId
export async function deleteEmployee(empUserId: string): Promise<{ success: boolean }> {
  try {
    // First delete the user from auth.users
    const { error: authError } = await supabaseAdmin.auth.admin.deleteUser(empUserId);
    if (authError) {
      console.error('Error deleting auth user:', authError);
      throw new Error('Could not delete user account');
    }

    // Then delete the employee from the employees table
    const { error: employeeError } = await supabase
      .from('employees')
      .delete()
      .eq('empUserId', empUserId);

    if (employeeError) {
      console.error('Error deleting employee:', employeeError);
      throw new Error('Could not delete employee');
    }

    return { success: true };
  } catch (error) {
    console.error('Delete operation failed:', error);
    throw error;
  }
}

// Function to swap employee positions
export async function swapEmployeePositions(
  currentRow: Employee,
  targetRow: Employee,
  direction: 'up' | 'down'
): Promise<void> {
  try {
    // First, get all employees to determine their positions
    const { data: employees, error: fetchError } = await supabase
      .from('employees')
      .select('*')
      .order('position');

    if (fetchError) throw fetchError;

    // Find the current positions
    const currentPosition = employees?.find(emp => emp.id === currentRow.id)?.position || 0;
    const targetPosition = employees?.find(emp => emp.id === targetRow.id)?.position || 0;

    // Update current row's position
    const { error: error1 } = await supabase
      .from('employees')
      .update({ position: targetPosition })
      .eq('id', currentRow.id);

    if (error1) throw error1;

    // Update target row's position
    const { error: error2 } = await supabase
      .from('employees')
      .update({ position: currentPosition })
      .eq('id', targetRow.id);

    if (error2) throw error2;
  } catch (error) {
    console.error(`Error swapping employees ${direction}:`, error);
    throw error;
  }
}

// Supabase realtime subscription setup
export function subscribeToEmployees(
  onUpdate: (payload: { new: Employee; old: Employee }) => void,
  onDelete: (payload: { old: Employee }) => void
) {
  const subscription = supabase
    .channel('employees-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'employees',
      },
      payload => {
        const typedPayload = payload as RealtimePostgresUpdatePayload<Employee>;
        switch (payload.eventType) {
          case 'UPDATE':
            if (typedPayload.new && typedPayload.old) {
              onUpdate({ new: typedPayload.new as Employee, old: typedPayload.old as Employee });
            }
            break;
          case 'DELETE':
            if (typedPayload.old) {
              onDelete({ old: typedPayload.old as Employee });
            }
            break;
        }
      }
    )
    .subscribe();

  return () => {
    subscription.unsubscribe();
  };
}
